import { ROLES, type Role } from '../access/role';
import { isBillingField } from '../order/accountsReview';
import { labDayOf } from '../time/labDay';

/**
 * Every notification the system sends, the group each is in, who receives it
 * and where. The request of 7 October 2026, sections 1 and 2.
 *
 * The API holds the same catalogue and the same rules under the same names,
 * `NotificationCatalog` and `NotificationRules`, and is the one that decides.
 * This file is what the screens read and what the mock server answers with.
 */

export const NOTIFICATION_CATEGORIES = ['case', 'edits', 'done', 'messages', 'reviews', 'system'] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_CATEGORY_LABEL: Record<NotificationCategory, string> = {
  case: 'Case changes',
  edits: 'Edits',
  done: 'Done & ready',
  messages: 'Messages',
  reviews: 'Reviews & approvals',
  system: 'System',
};

export function isNotificationCategory(value: string | null | undefined): value is NotificationCategory {
  return (NOTIFICATION_CATEGORIES as readonly string[]).includes(value ?? '');
}

/** One kind of notification. */
export interface NotificationTypeInfo {
  key: string;
  category: NotificationCategory;
  title: string;
  /** When it is sent, in a line, for the screens where it is switched on or off. */
  about: string;
  /** The roles that receive it until management says otherwise. */
  roles: readonly Role[];
  /** Whether those roles may turn it off, until management says otherwise. */
  mandatory: boolean;
}

const EVERYBODY: readonly Role[] = ROLES;
const NOBODY: readonly Role[] = [];
const FLOOR: readonly Role[] = ['management', 'reception', 'cadcam', 'tech'];
const BENCHES: readonly Role[] = ['management', 'cadcam', 'tech'];
const MAKERS: readonly Role[] = ['cadcam', 'tech'];

export const NOTIFICATION_TYPES: readonly NotificationTypeInfo[] = [
  { key: 'case.assigned', category: 'case', title: 'Assigned to you', about: 'A case is given to you.', roles: FLOOR, mandatory: true },
  { key: 'case.takenOver', category: 'case', title: 'Taken over', about: 'Somebody takes a case you were holding.', roles: FLOOR, mandatory: true },
  { key: 'case.rework', category: 'case', title: 'Sent for rework', about: 'A case fails Final Check and comes back to you or your department.', roles: BENCHES, mandatory: true },
  { key: 'case.flagged', category: 'case', title: 'Flagged', about: 'A case is flagged and sent to you or your department.', roles: BENCHES, mandatory: false },
  { key: 'case.resolved', category: 'case', title: 'Rework resolved', about: 'A case you sent for rework is put right.', roles: BENCHES, mandatory: false },
  { key: 'case.undone', category: 'case', title: 'Taken back', about: 'An action you were told about is undone.', roles: FLOOR, mandatory: false },
  { key: 'case.held', category: 'case', title: 'On hold or released', about: 'A case you are working on is put on hold, or released.', roles: BENCHES, mandatory: false },
  { key: 'case.urgent', category: 'case', title: 'Marked urgent', about: 'A case you are working on is marked urgent, or no longer is.', roles: BENCHES, mandatory: false },
  { key: 'case.clockStopped', category: 'case', title: 'Clock stopped', about: 'Your clock on a case is stopped by management, by a hold or at the end of the day.', roles: FLOOR, mandatory: true },
  { key: 'case.moved', category: 'case', title: 'Moved to another department', about: 'A case moves from one department to the next.', roles: NOBODY, mandatory: false },

  { key: 'edit.items', category: 'edits', title: 'Items changed', about: 'The items, teeth or quantities of a case you are working on are changed.', roles: MAKERS, mandatory: false },
  { key: 'edit.dueDate', category: 'edits', title: 'Due date changed', about: 'The start or delivery date of a case you are working on is moved.', roles: MAKERS, mandatory: false },
  { key: 'edit.instructions', category: 'edits', title: 'Instructions changed', about: 'The instructions of a case you are working on are changed.', roles: MAKERS, mandatory: false },
  { key: 'edit.details', category: 'edits', title: 'Job order edited', about: 'Anything else on a case you are working on is changed.', roles: MAKERS, mandatory: false },

  { key: 'done.stage', category: 'done', title: 'Marked done', about: 'A department finishes its work on a case.', roles: NOBODY, mandatory: false },
  { key: 'done.ready', category: 'done', title: 'Ready to deliver', about: 'A case passes Final Check, and waits for payment and dispatch.', roles: ['reception', 'accountant'], mandatory: false },
  { key: 'done.shipped', category: 'done', title: 'Shipped', about: 'A case leaves the lab.', roles: ['management'], mandatory: false },
  { key: 'done.received', category: 'done', title: 'Received', about: 'The customer has the case.', roles: ['management', 'reception'], mandatory: false },

  { key: 'message.note', category: 'messages', title: 'New message', about: 'Somebody writes in Lab Notes on a case you are working on.', roles: FLOOR, mandatory: false },
  { key: 'message.private', category: 'messages', title: 'Private note for you', about: 'Somebody writes to you alone on a case.', roles: EVERYBODY, mandatory: true },

  { key: 'review.waiting', category: 'reviews', title: 'Waiting for review', about: "A change to a case's bill waits for Accounts.", roles: ['accountant'], mandatory: true },
  // A case that is not fully paid has reached Ceramic. The request of 8 October 2026 (fourth).
  { key: 'review.payment', category: 'reviews', title: 'Payment to follow up', about: 'A case that is not fully paid reaches Ceramic.', roles: ['accountant'], mandatory: true },
  { key: 'review.completed', category: 'reviews', title: 'Review completed', about: 'Accounts reviewed a change you made.', roles: ['management', 'reception', 'cadcam'], mandatory: false },
  { key: 'approval.deletion', category: 'reviews', title: 'Deletion to approve', about: 'Somebody asks to delete a case.', roles: ['management'], mandatory: true },
  { key: 'approval.answer', category: 'reviews', title: 'Deletion decided', about: 'A deletion you asked for is approved or refused.', roles: ['management', 'reception'], mandatory: false },

  { key: 'system.access', category: 'system', title: 'Your account changed', about: 'Management changes your role, your access or your password.', roles: EVERYBODY, mandatory: true },
];

const BY_KEY = new Map(NOTIFICATION_TYPES.map((type) => [type.key, type]));

/**
 * The six types rows were written with before, which rows already stored still
 * carry: the category each is read in and what it is called.
 */
const BEFORE: Readonly<Record<string, { category: NotificationCategory; title: string }>> = {
  assign: { category: 'case', title: 'Assigned to you' },
  rework: { category: 'case', title: 'Sent for rework' },
  alert: { category: 'case', title: 'Case changed' },
  ready: { category: 'done', title: 'Ready to deliver' },
  approval: { category: 'reviews', title: 'Deletion' },
  note: { category: 'messages', title: 'New message' },
};

export function notificationType(key: string | null | undefined): NotificationTypeInfo | undefined {
  return BY_KEY.get(key ?? '');
}

export function notificationCategoryOf(type: string): NotificationCategory {
  return BY_KEY.get(type)?.category ?? BEFORE[type]?.category ?? 'system';
}

export function notificationTitleOf(type: string): string {
  return BY_KEY.get(type)?.title ?? BEFORE[type]?.title ?? 'Notice';
}

/** Every type a row may carry that reads in this category, the old six among them. */
export function notificationTypesIn(category: NotificationCategory): string[] {
  return [
    ...NOTIFICATION_TYPES.filter((type) => type.category === category).map((type) => type.key),
    ...Object.entries(BEFORE)
      .filter(([, before]) => before.category === category)
      .map(([key]) => key),
  ];
}

/**
 * The type of the one notification an edit sends, by what it changed: the items
 * first, then the instructions, then anything else. One edit is one
 * notification. The dates are moved by their own command, which sends
 * `edit.dueDate` itself.
 */
export function editNotificationType(fields: readonly string[]): string {
  if (fields.some((field) => isBillingField(field) || field === 'span')) return 'edit.items';
  return fields.includes('instructions') ? 'edit.instructions' : 'edit.details';
}

/** Whether a notification opens the case's Lab Notes rather than the case: a message, or a case given to you. */
export function opensLabNotes(type: string): boolean {
  return notificationCategoryOf(type) === 'messages' || type === 'case.assigned' || type === 'assign';
}

// ------------------------------------------------------------------ who gets what

/** Where a type stands for somebody. */
export interface NotificationSetting {
  receives: boolean;
  inApp: boolean;
  push: boolean;
  mandatory: boolean;
}

/** What management decided about one type, for a role or for one person. */
export interface NotificationRule extends NotificationSetting {
  type: string;
}

/** What a person chose for themselves. A null home list is somebody who never chose. */
export interface NotificationChoices {
  off: readonly string[];
  home: readonly string[] | null;
}

export const NO_NOTIFICATION_CHOICES: NotificationChoices = { off: [], home: null };

export const NOTIFICATION_OFF: NotificationSetting = { receives: false, inApp: true, push: false, mandatory: false };

/** What the API refuses with, word for word. */
export const NOTIFICATION_REFUSAL = {
  onlyManagement: 'Only management may change who is notified of what.',
  badType: 'That is not a notification this system sends.',
  badRole: 'That is not a role.',
  noChannel: 'Choose where it appears: in the app, as a push notification, or both.',
  mandatory: 'That notification cannot be turned off.',
  notYours: 'That is not a notification you receive.',
} as const;

/** Sent at all: received, and shown somewhere. */
export function delivers(setting: NotificationSetting): boolean {
  return setting.receives && (setting.inApp || setting.push);
}

/** The catalogue's own answer for a role: where a lab starts. */
export function defaultNotificationSetting(type: NotificationTypeInfo, role: Role): NotificationSetting {
  const receives = type.roles.includes(role);
  return { receives, inApp: true, push: false, mandatory: receives && type.mandatory };
}

function settingOf(rule: NotificationSetting): NotificationSetting {
  return {
    receives: rule.receives,
    inApp: rule.inApp,
    push: rule.push,
    mandatory: rule.receives && rule.mandatory,
  };
}

/** What a role has for a type: management's rule for it, or the catalogue's. */
export function roleNotificationSetting(
  type: NotificationTypeInfo,
  role: Role,
  roleRule: NotificationSetting | null | undefined,
): NotificationSetting {
  return roleRule == null ? defaultNotificationSetting(type, role) : settingOf(roleRule);
}

/** What a person has for a type, before their own choices: their own rule, or their role's. */
export function userNotificationSetting(
  type: NotificationTypeInfo,
  role: Role,
  roleRule: NotificationSetting | null | undefined,
  userRule: NotificationSetting | null | undefined,
): NotificationSetting {
  return userRule == null ? roleNotificationSetting(type, role, roleRule) : settingOf(userRule);
}

export function sameNotificationSetting(a: NotificationSetting, b: NotificationSetting): boolean {
  return a.receives === b.receives && a.inApp === b.inApp && a.push === b.push && a.mandatory === b.mandatory;
}

/**
 * Whether a notification of this type is written for this person, and where it
 * shows. A type they turned off is not sent unless it is mandatory for them. A
 * type nobody listed, one of the old six among them, is sent in the app as it
 * always was.
 */
export function decideNotification(
  typeKey: string,
  role: Role,
  roleRule: NotificationSetting | null | undefined,
  userRule: NotificationSetting | null | undefined,
  choices: NotificationChoices,
): NotificationSetting {
  const type = BY_KEY.get(typeKey);
  if (type === undefined) return { receives: true, inApp: true, push: false, mandatory: false };

  const setting = userNotificationSetting(type, role, roleRule, userRule);
  if (!delivers(setting)) return NOTIFICATION_OFF;
  return !setting.mandatory && choices.off.includes(typeKey) ? NOTIFICATION_OFF : setting;
}

/** A rule management may not keep, and why, or null. */
export function notificationRuleRefusal(rule: {
  type: string | null | undefined;
  receives: boolean;
  inApp: boolean;
  push: boolean;
}): string | null {
  if (!BY_KEY.has(rule.type ?? '')) return NOTIFICATION_REFUSAL.badType;
  return rule.receives && !rule.inApp && !rule.push ? NOTIFICATION_REFUSAL.noChannel : null;
}

/**
 * A person's own choices they may not keep, and why, or null: every type must
 * be one they receive, and one that is mandatory for them stays on.
 */
export function notificationChoicesRefusal(
  choices: NotificationChoices,
  mine: Readonly<Record<string, NotificationSetting>>,
): string | null {
  for (const type of [...choices.off, ...(choices.home ?? [])]) {
    const setting = mine[type];
    if (setting === undefined || !delivers(setting)) return NOTIFICATION_REFUSAL.notYours;
  }
  return choices.off.some((type) => mine[type]!.mandatory) ? NOTIFICATION_REFUSAL.mandatory : null;
}

/** Said in the log for one changed rule: "Shipped: on, in the app and push, mandatory". */
export function describeNotificationRule(type: NotificationTypeInfo, setting: NotificationSetting): string {
  if (!setting.receives) return `${type.title}: off`;
  const where = setting.inApp && setting.push ? 'in the app and push' : setting.push ? 'push' : 'in the app';
  return `${type.title}: on, ${where}${setting.mandatory ? ', mandatory' : ''}`;
}

// ------------------------------------------------------------------ reading them

export interface NotificationDay<Row> {
  /** `today`, `yesterday`, or the lab day itself, YYYY-MM-DD. */
  day: string;
  items: Row[];
}

/**
 * The rows gathered by the lab's day, in the order they already stand: Today,
 * Yesterday, then each earlier date. The request of 7 October 2026, section 2.
 */
export function groupNotificationsByDay<Row extends { createdAt: string }>(
  rows: readonly Row[],
  today: string,
): NotificationDay<Row>[] {
  const yesterday = labDayOf(Date.parse(`${today}T12:00:00.000Z`) - 24 * 60 * 60 * 1000);
  const days: NotificationDay<Row>[] = [];
  for (const row of rows) {
    const on = labDayOf(row.createdAt);
    const day = on === today ? 'today' : on === yesterday ? 'yesterday' : on;
    const last = days[days.length - 1];
    if (last?.day === day) last.items.push(row);
    else days.push({ day, items: [row] });
  }
  return days;
}
